import * as React from 'react';
import { observable, action } from 'mobx';
import { observer, inject } from 'mobx-react';

// Assets
const QuestionSVG = require('../../assets/illustrations/question.svg') as string;

// Services
import {
  IGetQuestionResponse,
  getQuestions,
  getQuestionComments,
  IGetQuestionsCommentResponse,
  createQuestionComments
} from '../../services/question';

// Types
import { IStore } from '../../store/Store';

export type IComment = IGetQuestionsCommentResponse;
export type IQuestion = IGetQuestionResponse;

// Components
import Box from '../../components/Box';
import CommentsDialog from '../../components/CommentsDialog';
import Container from '../../components/Container';
import { FixedTopBar } from '../../components/Layout';
import { CircularProgress } from '../../components/Progress';
import StatusCard from '../../components/StatusCard';
import Toolbar from '../../components/Toolbar';
import Typography from '../../components/Typography';

import QuestionCard from './QuestionCard';
import QuestionDialog from './QuestionDialog';

// Component
interface IProps {
  store?: IStore;
}

class AppNetworkQuestions extends React.Component<IProps> {
  @observable
  questionModalState: {
    open: boolean;
    questionId: string | null;
    networkId: string | null;
  } = { open: false, questionId: null, networkId: null };
  @observable selectedQuestion: IQuestion | null = null;
  @observable
  commentDialogState: {
    open: boolean;
    questionId: string | null;
    networkId: string | null;
  } = {
    open: false,
    questionId: null,
    networkId: null
  };

  @observable.shallow comments: IComment[] | null = null;
  @observable.shallow questions: IQuestion[] | null = null;

  componentDidMount() {
    this.getQuestions();
  }

  async getQuestions() {
    const networkId = this.props.store!.networkStore.network!.networkId;
    this.questions = await getQuestions({ params: { networkId } });
  }

  async getComments() {
    this.comments = await getQuestionComments({
      params: {
        networkId: this.commentDialogState.networkId!
      },
      query: {
        questionId: this.commentDialogState.questionId!
      }
    });
  }

  async createComment(comment: string) {
    await createQuestionComments({
      params: {
        networkId: this.commentDialogState.networkId!
      },
      query: {
        questionId: this.commentDialogState.questionId!
      },
      body: {
        comment: comment
      }
    });

    this.getComments();
  }

  handleQuestionOpen = (question: IQuestion) => {
    this.questionModalState.open = true;
    this.questionModalState.networkId = question.networkId;
    this.questionModalState.questionId = question.questionId;
  };

  handleQuestionDialogClose = () => {
    this.questionModalState.open = false;
  };

  handleQuestionDialogExited = () => {
    this.selectedQuestion = null;
  };

  handleQuestionDelete = async () => {
    this.getQuestions();
    this.handleQuestionDialogClose();
  };

  @action
  handleRequestComment = (questionId: string, networkId: string) => () => {
    this.commentDialogState.open = true;
    this.commentDialogState.questionId = questionId;
    this.commentDialogState.networkId = networkId;
    this.getComments();
  };

  handleCommentDialogSubmit = async (comment: string) => {
    await this.createComment(comment);
  };

  handleCommentDialogClose = () => {
    this.commentDialogState.open = false;
  };

  @action
  handleCommentDialogExited = () => {
    this.commentDialogState.open = false;
    this.commentDialogState.questionId = null;
    this.commentDialogState.networkId = null;
    this.comments = null;
  };

  render() {
    if (this.questions === null) {
      return (
        <Box pa={5} align="center">
          <CircularProgress />
        </Box>
      );
    }

    const role = this.props.store!.networkStore.person!.role;

    return (
      <div>
        <FixedTopBar size="md" horizontalPadded={true}>
          <Toolbar>
            <Typography type="title">Questions</Typography>
          </Toolbar>
        </FixedTopBar>
        <Container size="md" padded={true}>
          <div>
            {this.questions.length ? (
              this.questions.map(item => (
                <QuestionCard
                  key={item.questionId}
                  question={item}
                  onOpen={this.handleQuestionOpen}
                  onRequestComment={this.handleRequestComment(
                    item.questionId,
                    item.networkId
                  )}
                />
              ))
            ) : (
              <StatusCard
                icon={QuestionSVG}
                headline={'No Questions'}
                subheading={'There has not been asked any questions yet.'}
              />
            )}
          </div>
          <QuestionDialog
            state={this.questionModalState}
            onClose={this.handleQuestionDialogClose}
            onDeleted={this.handleQuestionDelete}
            userRole={role}
          />
          <CommentsDialog
            comments={this.comments}
            state={this.commentDialogState}
            onSubmit={this.handleCommentDialogSubmit}
            onClose={this.handleCommentDialogClose}
            onExited={this.handleCommentDialogExited}
          />
        </Container>
      </div>
    );
  }
}

// Exports
export default inject('store')(observer(AppNetworkQuestions));



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/views/AppNetworkQuestions/AppNetworkQuestions.tsx